import * as React from 'react'
import clsx from 'clsx'
import { SxProps, sx as sxFunc } from '../sx'
import { PaperProps, Paper } from '../Paper'
import { ModalProps, Modal } from '../Modal'
import { TransitionProps } from '../transitions'
import { Grow } from '../Grow'
import { useForkRef } from '../../hooks'
import { ownerDocument, ownerWindow, debounce } from '../../utils'
import styles from './index.module.scss'

export interface PopoverClasses {
  /** Styles applied to the root element. */
  root: string
  /** Styles applied to the Paper component. */
  paper: string
}

export interface PopoverOrigin {
  vertical: 'top' | 'center' | 'bottom' | number
  horizontal: 'left' | 'center' | 'right' | number
}

export interface PopoverPosition {
  top: number
  left: number
}

export type PopoverReference = 'anchorEl' | 'anchorPosition' | 'none'

export interface PopoverActions {
  updatePosition(): void
}

export interface PopoverProps extends Omit<ModalProps, 'children'> {
  /**
   * A ref for imperative actions.
   * It currently only supports updatePosition() action.
   */
  action?: React.Ref<PopoverActions>
  /**
   * An HTML element, or a function that returns one.
   * It's used to set the position of the popover.
   */
  anchorEl?: null | Element | ((element: Element) => Element)
  /**
   * This is the point on the anchor where the popover's
   * `anchorEl` will attach to. This is not used when the
   * anchorReference is 'anchorPosition'.
   *
   * Options:
   * vertical: [top, center, bottom];
   * horizontal: [left, center, right].
   * @default {
   *   vertical: 'top',
   *   horizontal: 'left',
   * }
   */
  anchorOrigin?: PopoverOrigin
  /**
   * This is the position that may be used to set the position of the popover.
   * The coordinates are relative to the application's client area.
   */
  anchorPosition?: PopoverPosition
  /**
   * This determines which anchor prop to refer to when setting
   * the position of the popover.
   * @default 'anchorEl'
   */
  anchorReference?: PopoverReference
  /**
   * The content of the component.
   */
  children?: React.ReactNode
  /**
   * Override or extend the styles applied to the component.
   */
  classes?: Partial<PopoverClasses>
  /**
   * An HTML element, component instance, or function that returns either.
   * The `container` will passed to the Modal component.
   *
   * By default, it uses the body of the anchorEl's top-level document object,
   * so it's simply `document.body` most of the time.
   */
  container?: ModalProps['container']
  /**
   * The elevation of the popover.
   * @default 2
   */
  elevation?: PaperProps['elevation']
  /**
   * Specifies how close to the edge of the window the popover can appear.
   * @default 16
   */
  marginThreshold?: number
  onClose?: ModalProps['onClose']
  /**
   * If `true`, the component is shown.
   */
  open: boolean
  /**
   * Props applied to the [`Paper`](/material-ui/api/paper/) element.
   * @default {}
   */
  PaperProps?: Partial<PaperProps>
  /**
   * The system prop that allows defining system overrides as well as additional CSS styles.
   */
  sx?: SxProps
  /**
   * This is the point on the popover which
   * will attach to the anchor's origin.
   *
   * Options:
   * vertical: [top, center, bottom, x(px)];
   * horizontal: [left, center, right, x(px)].
   * @default {
   *   vertical: 'top',
   *   horizontal: 'left',
   * }
   */
  transformOrigin?: PopoverOrigin
  /**
   * The component used for the transition.
   * [Follow this guide](/material-ui/transitions/#transitioncomponent-prop) to learn more about the requirements for this component.
   * @default Grow
   */
  TransitionComponent?: React.JSXElementConstructor<
    TransitionProps & { children: React.ReactElement<any, any> }
  >
  /**
   * Set to 'auto' to automatically calculate transition time based on height.
   * @default 'auto'
   */
  transitionDuration?: TransitionProps['timeout'] | 'auto'
  /**
   * Props applied to the transition element.
   * By default, the element is based on this [`Transition`](http://reactcommunity.org/react-transition-group/transition/) component.
   * @default {}
   */
  TransitionProps?: TransitionProps
}

export function getOffsetTop(
  rect: DOMRect,
  vertical: PopoverOrigin['vertical']
) {
  let offset = 0

  if (typeof vertical === 'number') {
    offset = vertical
  } else if (vertical === 'center') {
    offset = rect.height / 2
  } else if (vertical === 'bottom') {
    offset = rect.height
  }

  return offset
}

export function getOffsetLeft(
  rect: DOMRect,
  horizontal: PopoverOrigin['horizontal']
) {
  let offset = 0

  if (typeof horizontal === 'number') {
    offset = horizontal
  } else if (horizontal === 'center') {
    offset = rect.width / 2
  } else if (horizontal === 'right') {
    offset = rect.width
  }

  return offset
}

function getTransformOriginValue(transformOrigin: PopoverOrigin) {
  return [transformOrigin.horizontal, transformOrigin.vertical]
    .map(n => (typeof n === 'number' ? `${n}px` : n))
    .join(' ')
}

function resolveAnchorEl(
  anchorEl?: null | Element | ((element: Element) => Element)
): Element | null {
  // @ts-ignore
  return typeof anchorEl === 'function' ? anchorEl() : anchorEl
}

export const Popover = React.forwardRef<HTMLDivElement, PopoverProps>(
  function Popover(props, ref) {
    const {
      action,
      anchorEl,
      classes,
      anchorOrigin = {
        vertical: 'top',
        horizontal: 'left',
      },
      anchorPosition,
      anchorReference = 'anchorEl',
      children,
      className,
      container: containerProp,
      elevation = 2,
      marginThreshold = 16,
      open,
      PaperProps = {},
      transformOrigin = {
        vertical: 'top',
        horizontal: 'left',
      },
      TransitionComponent = Grow,
      transitionDuration: transitionDurationProp = 'auto',
      TransitionProps: { onEntering, ...TransitionProps } = {},
      sx,
      ...other
    } = props
    const paperRef = React.useRef()
    // @ts-ignore
    const handlePaperRef = useForkRef(paperRef, PaperProps.ref)

    // const ownerState = {
    //   ...props,
    //   anchorOrigin,
    //   anchorReference,
    //   elevation,
    //   marginThreshold,
    //   PaperProps,
    //   transformOrigin,
    //   TransitionComponent,
    //   transitionDuration: transitionDurationProp,
    //   TransitionProps,
    // }

    // Returns the top/left offset of the position
    // to attach to on the anchor element (or body if none is provided)
    const getAnchorOffset = React.useCallback(() => {
      if (anchorReference === 'anchorPosition') {
        if (process.env.NODE_ENV !== 'production') {
          if (!anchorPosition) {
            console.error(
              'You need to provide a `anchorPosition` prop when using ' +
                '<Popover anchorReference="anchorPosition" />.'
            )
          }
        }
        return anchorPosition
      }

      const resolvedAnchorEl = resolveAnchorEl(anchorEl)

      // If an anchor element wasn't provided, just use the parent body element of this Popover
      const anchorElement =
        resolvedAnchorEl && resolvedAnchorEl.nodeType === 1
          ? resolvedAnchorEl
          : ownerDocument(paperRef.current).body
      const anchorRect = anchorElement.getBoundingClientRect()

      return {
        top: anchorRect.top + getOffsetTop(anchorRect, anchorOrigin.vertical),
        left:
          anchorRect.left + getOffsetLeft(anchorRect, anchorOrigin.horizontal),
      }
    }, [
      anchorEl,
      anchorOrigin.horizontal,
      anchorOrigin.vertical,
      anchorPosition,
      anchorReference,
    ])

    // Returns the base transform origin using the element
    const getTransformOrigin = React.useCallback(
      (elemRect: DOMRect) => {
        return {
          vertical: getOffsetTop(elemRect, transformOrigin.vertical),
          horizontal: getOffsetLeft(elemRect, transformOrigin.horizontal),
        }
      },
      [transformOrigin.horizontal, transformOrigin.vertical]
    )

    const getPositioningStyle = React.useCallback(
      (element: HTMLElement) => {
        const elemRect = {
          width: element.offsetWidth,
          height: element.offsetHeight,
        }

        // Get the transform origin point on the element itself
        const elemTransformOrigin = getTransformOrigin(elemRect as DOMRect)

        if (anchorReference === 'none') {
          return {
            top: null,
            left: null,
            transformOrigin: getTransformOriginValue(elemTransformOrigin),
          }
        }

        // Get the offset of the anchoring element
        const anchorOffset = getAnchorOffset()

        // Calculate element positioning
        let top = anchorOffset!.top - elemTransformOrigin.vertical
        let left = anchorOffset!.left - elemTransformOrigin.horizontal
        const bottom = top + elemRect.height
        const right = left + elemRect.width

        // Use the parent window of the anchorEl if provided
        const containerWindow = ownerWindow(resolveAnchorEl(anchorEl)!)

        // Window thresholds taking required margin into account
        const heightThreshold = containerWindow.innerHeight - marginThreshold
        const widthThreshold = containerWindow.innerWidth - marginThreshold

        // Check if the vertical axis needs shifting
        if (top < marginThreshold) {
          const diff = top - marginThreshold
          top -= diff
          elemTransformOrigin.vertical += diff
        } else if (bottom > heightThreshold) {
          const diff = bottom - heightThreshold
          top -= diff
          elemTransformOrigin.vertical += diff
        }

        if (process.env.NODE_ENV !== 'production') {
          if (
            elemRect.height > heightThreshold &&
            elemRect.height &&
            heightThreshold
          ) {
            console.error(
              [
                'The popover component is too tall.',
                `Some part of it can not be seen on the screen (${
                  elemRect.height - heightThreshold
                }px).`,
                'Please consider adding a `max-height` to improve the user-experience.',
              ].join('\n')
            )
          }
        }

        // Check if the horizontal axis needs shifting
        if (left < marginThreshold) {
          const diff = left - marginThreshold
          left -= diff
          elemTransformOrigin.horizontal += diff
        } else if (right > widthThreshold) {
          const diff = right - widthThreshold
          left -= diff
          elemTransformOrigin.horizontal += diff
        }

        return {
          top: `${Math.round(top)}px`,
          left: `${Math.round(left)}px`,
          transformOrigin: getTransformOriginValue(elemTransformOrigin),
        }
      },
      [
        anchorEl,
        anchorReference,
        getAnchorOffset,
        getTransformOrigin,
        marginThreshold,
      ]
    )

    const [isPositioned, setIsPositioned] = React.useState(open)

    const setPositioningStyles = React.useCallback(() => {
      const element = paperRef.current! as HTMLDivElement

      if (!element) {
        return
      }

      const positioning = getPositioningStyle(element)

      if (positioning.top !== null) {
        element.style.top = positioning.top
      }
      if (positioning.left !== null) {
        element.style.left = positioning.left
      }
      element.style.transformOrigin = positioning.transformOrigin
      setIsPositioned(true)
    }, [getPositioningStyle])

    const handleEntering = (element: HTMLElement, isAppearing: boolean) => {
      if (onEntering) {
        onEntering(element, isAppearing)
      }

      setPositioningStyles()
    }

    const handleExited = () => {
      setIsPositioned(false)
    }

    React.useEffect(() => {
      if (open) {
        setPositioningStyles()
      }
    })

    React.useImperativeHandle(
      action,
      () =>
        open
          ? {
              updatePosition: () => {
                setPositioningStyles()
              },
            }
          : ({} as PopoverActions),
      [open, setPositioningStyles]
    )

    React.useEffect(() => {
      if (!open) {
        return undefined
      }

      const handleResize = debounce(() => {
        setPositioningStyles()
      })

      const containerWindow = ownerWindow(anchorEl as any)
      containerWindow.addEventListener('resize', handleResize)
      return () => {
        handleResize.clear()
        containerWindow.removeEventListener('resize', handleResize)
      }
    }, [anchorEl, open, setPositioningStyles])

    let transitionDuration: typeof transitionDurationProp | undefined =
      transitionDurationProp

    if (
      transitionDurationProp === 'auto' &&
      // @ts-ignore
      !TransitionComponent.factorSupportAuto
    ) {
      transitionDuration = undefined
    }

    // If the container prop is provided, use that
    // If the anchorEl prop is provided, use its parent body element as the container
    // If neither are provided let the Modal take care of choosing the container
    const container =
      containerProp ||
      (anchorEl ? ownerDocument(resolveAnchorEl(anchorEl)).body : undefined)

    return (
      <Modal
        slotProps={{ backdrop: { invisible: true } }}
        className={clsx(classes?.root, className, sxFunc(sx))}
        container={container}
        open={open}
        ref={ref}
        {...other}
      >
        <TransitionComponent
          appear
          in={open}
          onEntering={handleEntering}
          onExited={handleExited}
          timeout={transitionDuration}
          {...TransitionProps}
        >
          <Paper
            elevation={elevation}
            {...PaperProps}
            // @ts-ignore
            ref={handlePaperRef}
            className={clsx(styles.paper, classes?.paper, PaperProps.className)}
            {...(isPositioned
              ? undefined
              : { style: { ...PaperProps.style, opacity: 0 } })}
          >
            {children}
          </Paper>
        </TransitionComponent>
      </Modal>
    )
  }
)
